import type { ILocation, ITag} from '@map-bldr/shared';
import { LocationListItem } from './LocationListItem';
import { useLocations } from '../hooks/locationHooks';
import { useTags } from '../hooks/tagHooks';
import { usePrefetchLocationTagLinks } from '../hooks/locationTagLinkHooks';
import { useQueryClient } from '@tanstack/react-query';

export interface ILocationListProps {
  selectedLocationId: string | null;
  selectedTagFilters: string[];
  onSelectLocation: (locationId: string) => void;
  onUnselectLocation: () => void;
  onRemoveLocation: (id: string) => void;
  onCreateAndApplyTag: (locationId: string, name: string) => void;
  onExport: () => void;
}

export function LocationList({
  selectedLocationId,
  selectedTagFilters,
  onSelectLocation,
  onUnselectLocation,
  onRemoveLocation,
  onCreateAndApplyTag,
  onExport
}: ILocationListProps) {
  const queryClient = useQueryClient();
  const { data: locations = [], isLoading: locationsLoading } = useLocations();
  const { data: availableTags = [], isLoading: tagsLoading } = useTags();

  // Prefetch all location tag links
  usePrefetchLocationTagLinks();

  // Filter locations based on selected tags (OR logic: show locations with ANY selected tag)
  // Always include the selected location, even if it doesn't match the filter
  const filteredLocations = selectedTagFilters.length === 0
    ? locations
    : locations.filter(location => {
        const locationTagIds = queryClient.getQueryData<string[]>(['locationTagLinks', location.id]) || [];
        return location.id === selectedLocationId ||
          locationTagIds.some(tagId => selectedTagFilters.includes(tagId));
      });

  const isLoading = locationsLoading || tagsLoading;
  const handleBackgroundClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only unselect if clicking directly on the background, not on child elements
    if (e.target === e.currentTarget) {
      onUnselectLocation();
    }
  };

  if (isLoading) {
    return (
      <div className="w-full h-full bg-background flex items-center justify-center">
        <p className="text-muted-foreground font-mono text-sm">Loading...</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full bg-background flex flex-col">
      <div className="flex-1 overflow-y-auto p-4" onClick={handleBackgroundClick}>
        {filteredLocations.length === 0 ? (
          <p className="text-muted-foreground italic font-mono text-sm">
            $ Click on the map to add locations
          </p>
        ) : (
          <div className="flex flex-col gap-2">
            {filteredLocations.map((location, index) => (
              <LocationListItem
                key={location.id}
                location={location}
                index={index}
                availableTags={availableTags}
                isSelected={location.id === selectedLocationId}
                onSelect={onSelectLocation}
                onRemove={onRemoveLocation}
                onCreateAndApplyTag={onCreateAndApplyTag}
              />
            ))}
          </div>
        )}
      </div>
      <div className="border-t border-border p-4 bg-card">
        <button
          onClick={onExport}
          disabled={locations.length === 0}
          className="bg-primary text-primary-foreground disabled:bg-muted disabled:text-muted-foreground border-none rounded-md px-6 py-3 text-sm font-bold cursor-pointer disabled:cursor-not-allowed w-full font-mono hover:opacity-90 transition-opacity"
        >
          Export Map →
        </button>
      </div>
    </div>
  );
}
